import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Heart, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../store/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAGS = ["All", "Family", "Blessings", "Love", "Magical Wisdom"];
const TAGS_UR = { All: "سب", Family: "خاندان", Blessings: "دعائیں", Love: "محبت", "Magical Wisdom": "جادوی نصیحت" };

export const WishesWall = () => {
  const { ur } = useLang();
  const [wishes, setWishes] = useState([]);
  const [tag, setTag] = useState("All");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [hearted, setHearted] = useState(() => new Set(JSON.parse(localStorage.getItem("hearted") || "[]")));

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/wishes`);
      setWishes(data);
    } catch {
      toast.error(ur ? "دعاؤں کا کنواں آرام کر رہا ہے — تھوڑی دیر بعد کوشش کریں" : "The wishing well is resting — try again soon");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!name.trim() || !message.trim()) {
      toast.error(ur ? "اپنا نام اور ہایا کے لیے دعا لکھیں" : "Add your name and a wish for Haya");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/wishes`, { name: name.trim(), message: message.trim(), tag: "Blessings", relation: "Guest" });
      setName("");
      setMessage("");
      toast.success(ur ? "آپ کی دعا ستاروں میں شامل ہو گئی" : "Your wish has joined the constellation");
      load();
    } catch {
      toast.error(ur ? "دعا محفوظ نہ ہو سکی — دوبارہ کوشش کریں" : "Could not place your wish — try again");
    } finally {
      setSending(false);
    }
  };

  const heart = async (id) => {
    if (hearted.has(id)) return;
    setWishes((w) => w.map((x) => (x.id === id ? { ...x, hearts: x.hearts + 1 } : x)));
    const next = new Set(hearted).add(id);
    setHearted(next);
    localStorage.setItem("hearted", JSON.stringify([...next]));
    try {
      await axios.post(`${API}/wishes/${id}/heart`);
    } catch {
      /* optimistic */
    }
  };

  const visible = tag === "All" ? wishes : wishes.filter((w) => w.tag === tag);

  return (
    <section id="wishes" className="relative py-28 sm:py-36 px-6" data-testid="wishes-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="eyebrow mb-4"
          >
            {ur ? "جادوی دعاؤں کی دیوار" : "The enchanted wishes wall"}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`font-display text-3xl sm:text-5xl text-amber-100 font-light ${ur ? "font-urdu" : ""}`}
            dir={ur ? "rtl" : "ltr"}
          >
            {ur ? "ایک آسمان تلے جمع دعائیں" : "Blessings gathered under one sky"}
          </motion.h2>
        </div>

        <div className="glass-card rounded-3xl p-6 sm:p-8 max-w-2xl mx-auto mb-14">
          <div className="flex flex-col gap-3">
            <input
              data-testid="wish-input-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder={ur ? "آپ کا نام" : "Your name"}
              dir={ur ? "rtl" : "ltr"}
              className="bg-white/5 border border-amber-200/25 rounded-full px-5 py-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-300/60 transition-colors duration-300"
            />
            <textarea
              data-testid="wish-input-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder={ur ? "ہایا نور جمال کے لیے دعا لکھیں…" : "Write a blessing for Haya Noor Jamal…"}
              dir={ur ? "rtl" : "ltr"}
              className="bg-white/5 border border-amber-200/25 rounded-3xl px-5 py-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-300/60 transition-colors duration-300 resize-none"
            />
            <button data-testid="wish-submit-btn" onClick={submit} disabled={sending} className="btn-gold justify-center disabled:opacity-60">
              <Send size={14} /> {sending ? (ur ? "دعا بھیجی جا رہی ہے…" : "Placing your star…") : ur ? "دعا لکھیں" : "Leave a Wish"}
            </button>
          </div>
        </div>

        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {TAGS.map((t) => (
            <button
              key={t}
              data-testid={`wish-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTag(t)}
              className={`px-4 py-1.5 rounded-full text-[0.65rem] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tag === t
                  ? "border-amber-300/60 text-amber-100 bg-amber-300/10"
                  : "border-amber-200/15 text-slate-500 hover:text-amber-200"
              }`}
            >
              {ur ? TAGS_UR[t] : t}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          <AnimatePresence>
            {visible.map((w, i) => (
              <motion.article
                key={w.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.08 }}
                className="glass-card rounded-2xl p-6 mb-5 break-inside-avoid"
                data-testid={`wish-card-${i}`}
              >
                <p className="font-display text-lg text-slate-200 leading-relaxed italic">“{w.message}”</p>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">{w.name}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{w.relation} · {ur ? TAGS_UR[w.tag] || w.tag : w.tag}</p>
                  </div>
                  <button
                    data-testid={`wish-heart-${i}`}
                    onClick={() => heart(w.id)}
                    className={`flex items-center gap-1.5 text-sm transition-colors duration-300 ${
                      hearted.has(w.id) ? "text-rose-300" : "text-slate-400 hover:text-rose-300"
                    }`}
                    aria-label="Send love to this wish"
                  >
                    <Heart size={15} className={hearted.has(w.id) ? "fill-rose-300" : ""} />
                    <span className="tabular-nums">{w.hearts}</span>
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
